import { supabase } from "@/integrations/supabase/client";

export type ErrorContext =
  | "checkout"
  | "order"
  | "wishlist"
  | "tracking"
  | "search"
  | "shopify"
  | "admin"
  | "edge_function"
  | "client"
  | "other";

export type ErrorSeverity = "info" | "warning" | "error" | "critical";

interface ReportOptions {
  context?: ErrorContext;
  severity?: ErrorSeverity;
  metadata?: Record<string, unknown>;
  autoRetryCount?: number;
}

const SESSION_KEY = "giz_session_id";
const RATE_LIMIT_KEY = "giz_err_rate";
const RATE_LIMIT_MAX = 10;
const RATE_LIMIT_WINDOW_MS = 60_000;

function getSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "anon";
  }
}

function checkRateLimit(): boolean {
  try {
    const raw = sessionStorage.getItem(RATE_LIMIT_KEY);
    const now = Date.now();
    const arr: number[] = raw ? JSON.parse(raw) : [];
    const recent = arr.filter((t) => now - t < RATE_LIMIT_WINDOW_MS);
    if (recent.length >= RATE_LIMIT_MAX) return false;
    recent.push(now);
    sessionStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(recent));
    return true;
  } catch {
    return true;
  }
}

export async function reportError(
  err: unknown,
  options: ReportOptions = {}
): Promise<void> {
  if (!checkRateLimit()) return;

  const message =
    err instanceof Error ? err.message : typeof err === "string" ? err : "Unknown error";
  const stack = err instanceof Error ? err.stack : undefined;

  try {
    const { data: userData } = await supabase.auth.getUser();
    await supabase.from("error_logs").insert([{
      user_id: userData?.user?.id ?? null,
      session_id: getSessionId(),
      context: options.context ?? "client",
      severity: options.severity ?? "error",
      message: message.slice(0, 5000),
      stack: stack?.slice(0, 20000) ?? null,
      metadata: (options.metadata ?? null) as never,
      url: typeof window !== "undefined" ? window.location.href : null,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      auto_retry_count: options.autoRetryCount ?? 0,
    }]);
  } catch (e) {
    // Don't fail the app because logging failed
    console.warn("Failed to report error:", e);
  }
}

interface RetryOptions {
  retries?: number;
  baseDelayMs?: number;
  shouldRetry?: (err: unknown) => boolean;
  onRetry?: (attempt: number, err: unknown) => void;
}

/**
 * Retry a network/async operation with exponential backoff.
 * Default: 3 attempts, 1s/3s/9s delays. Retries on network/5xx errors.
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const retries = options.retries ?? 3;
  const baseDelay = options.baseDelayMs ?? 1000;
  const shouldRetry = options.shouldRetry ?? defaultShouldRetry;

  let lastErr: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (attempt === retries || !shouldRetry(err)) throw err;
      options.onRetry?.(attempt + 1, err);
      await new Promise((r) => setTimeout(r, baseDelay * Math.pow(3, attempt)));
    }
  }
  throw lastErr;
}

function defaultShouldRetry(err: unknown): boolean {
  const msg = err instanceof Error ? err.message.toLowerCase() : String(err).toLowerCase();
  if (msg.includes("failed to fetch") || msg.includes("network")) return true;
  if (msg.includes("timeout") || msg.includes("aborted")) return true;
  // Retry on 5xx
  if (/\b5\d{2}\b/.test(msg)) return true;
  // Retry on 429 (rate limit) and 401 (expired token)
  if (msg.includes("429") || msg.includes("401")) return true;
  return false;
}

/**
 * Install global error handlers (call once at app startup).
 */
export function installGlobalErrorHandlers(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("error", (event) => {
    reportError(event.error ?? event.message, {
      context: "client",
      severity: "error",
      metadata: { source: "window.error", filename: event.filename, lineno: event.lineno },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    reportError(event.reason, {
      context: "client",
      severity: "error",
      metadata: { source: "unhandledrejection" },
    });
  });
}